// Shared, non-visual helpers used by both AdminPortal.jsx (exam-config +
// status tabs) and AdminSettings.jsx (Grades/Subjects/Target Exams/
// Generate Question Prompt/Upload Questions) - split out so the two pages
// don't duplicate this logic (2026-08-07 admin restructure, docs/DECISIONS.md).

// Duration: dropdown in 30-minute steps, 30 mins up to a 3-hour max (user
// request - not an SRS-stated bound, just the practical exam-length range
// this form should offer).
export const DURATION_OPTIONS = [30, 60, 90, 120, 150, 180];

export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} mins`;
  if (m === 0) return `${h} hr${h > 1 ? 's' : ''}`;
  return `${h} hr${h > 1 ? 's' : ''} ${m} mins`;
}

// Fallback grade options for a target exam with no grade range set (i.e.
// not grade-gated, e.g. GATE) - same 6-12 span the old hardcoded
// DEFAULT_GRADE_RANGE used before this became admin-editable.
export function gradeOptionsFor(targetExamName, targetExams) {
  const exam = targetExams.find((e) => e.name === targetExamName);
  if (!exam || exam.grade_min == null || exam.grade_max == null) {
    return ['6', '7', '8', '9', '10', '11', '12'];
  }
  const options = [];
  for (let g = exam.grade_min; g <= exam.grade_max; g += 1) options.push(String(g));
  return options;
}

// Per-target-exam subject defaults and whether Topics/Sub-topics are
// required - frontend UX only (the master data itself has no per-exam
// subject mapping; grade ranges are already admin-editable via Settings ->
// Grades, so they're deliberately not duplicated here). User-specified
// 2026-08-06 (docs/DECISIONS.md, "Admin exam-config form: per-target-exam
// subject/topic rules"). A target exam name not in this map falls back to
// today's behavior (no subject default, topics required) - matching by
// exact name, so it only takes effect once Settings -> Target Exams has an
// exam named exactly one of these.
export const TARGET_EXAM_RULES = {
  'JEE Foundation': {
    subjects: ['Mathematics', 'Physics', 'Chemistry', 'Biology'],
    topicsRequired: true,
  },
  'JEE Mains': {
    subjects: ['Mathematics', 'Physics', 'Chemistry'],
    topicsRequired: true,
  },
  'JEE Advanced': {
    subjects: ['Mathematics', 'Physics', 'Chemistry'],
    topicsRequired: true,
  },
  NEET: {
    subjects: ['Physics', 'Chemistry', 'Biology'],
    topicsRequired: true,
  },
};

export function topicsRequiredFor(targetExamName) {
  return TARGET_EXAM_RULES[targetExamName]?.topicsRequired ?? true;
}

// Same per-exam rule, applied to the single-select Subject dropdowns in the
// Generate Question Prompt / Upload Questions tools (docs/DECISIONS.md,
// 2026-08-06 - user asked for the exam-config form's rules to apply there
// too). Falls back to the full subjects list for an unmapped exam.
export function subjectOptionsFor(targetExamName, allSubjects) {
  const rule = TARGET_EXAM_RULES[targetExamName];
  if (!rule) return allSubjects;
  return allSubjects.filter((s) => rule.subjects.includes(s.name));
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function splitList(value) {
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export async function readErrorDetail(response, fallback) {
  const body = await response.json().catch(() => ({}));
  return body.detail || fallback;
}

export const EMPTY_FORM = {
  target_exam: '',
  grade: '',
  subjects: [],
  topics: '',
  sub_topics: '',
  easy_percent: '20',
  medium_percent: '50',
  hard_percent: '30',
  exam_date: '',
  exam_time: '09:00',
  duration_minutes: '180',
  total_marks: '300',
  question_count: '',
  student_email: '',
  father_email: '',
};

export function validateForm(form) {
  const errors = {};

  if (!form.target_exam) errors.target_exam = 'Select a target exam.';
  if (!form.grade) errors.grade = 'Select a grade.';
  if (!form.subjects.length) errors.subjects = 'Select at least one subject.';
  if (topicsRequiredFor(form.target_exam) && !splitList(form.topics).length) {
    errors.topics = 'At least one topic is required.';
  }

  const diffSum = Number(form.easy_percent) + Number(form.medium_percent) + Number(form.hard_percent);
  if (diffSum !== 100) errors.difficulty = `Easy/Medium/Hard must sum to 100% (currently ${diffSum}%).`;

  if (!form.exam_date) {
    errors.exam_date = 'Exam date is required.';
  } else if (form.exam_date < todayIso()) {
    errors.exam_date = 'Exam date cannot be in the past.';
  }

  if (!form.exam_time) errors.exam_time = 'Exam time is required.';

  if (!DURATION_OPTIONS.includes(Number(form.duration_minutes))) {
    errors.duration_minutes = 'Pick a valid duration.';
  }

  const marks = Number(form.total_marks);
  if (!form.total_marks || Number.isNaN(marks) || marks <= 0) {
    errors.total_marks = 'Total marks is required.';
  } else if (marks % 10 !== 0) {
    errors.total_marks = 'Total marks must be a multiple of 10.';
  }

  const questionCount = Number(form.question_count);
  if (!form.question_count || Number.isNaN(questionCount) || questionCount <= 0) {
    errors.question_count = 'Number of questions is required.';
  } else if (!Number.isInteger(questionCount)) {
    errors.question_count = 'Number of questions must be a whole number.';
  }

  if (!form.student_email || !EMAIL_RE.test(form.student_email)) {
    errors.student_email = 'Enter a valid student email address.';
  }
  if (!form.father_email || !EMAIL_RE.test(form.father_email)) {
    errors.father_email = 'Enter a valid parent/guardian email address.';
  }

  return errors;
}

export function buildSubmission(form) {
  return {
    target_exam: form.target_exam,
    grade: form.grade,
    subjects: form.subjects,
    topics: splitList(form.topics),
    sub_topics: splitList(form.sub_topics),
    difficulty_distribution: {
      easy: Number(form.easy_percent),
      medium: Number(form.medium_percent),
      hard: Number(form.hard_percent),
    },
    exam_date: form.exam_date,
    exam_time: form.exam_time.length === 5 ? `${form.exam_time}:00` : form.exam_time,
    duration_minutes: Number(form.duration_minutes),
    total_marks: Number(form.total_marks),
    question_count: Number(form.question_count),
    student_email: form.student_email,
    father_email: form.father_email,
  };
}

// Reverse of buildSubmission - pre-fills the form when the admin clicks
// Edit on an existing config (docs/DECISIONS.md, 2026-08-07 "admin
// exam-config edit/delete/recompile").
export function configToForm(config) {
  return {
    target_exam: config.target_exam,
    grade: config.grade,
    subjects: config.subjects,
    topics: config.topics.join(', '),
    sub_topics: config.sub_topics.join(', '),
    easy_percent: String(config.difficulty_distribution.easy),
    medium_percent: String(config.difficulty_distribution.medium),
    hard_percent: String(config.difficulty_distribution.hard),
    exam_date: config.exam_date,
    exam_time: config.exam_time.slice(0, 5),
    duration_minutes: String(config.duration_minutes),
    total_marks: String(config.total_marks),
    question_count: config.question_count == null ? '' : String(config.question_count),
    student_email: config.student_email,
    father_email: config.father_email,
  };
}

export const STATUS_STYLES = {
  pending: 'text-text-slate border-gray-300 bg-gray-100',
  generating: 'text-brand-blue border-brand-blue/40 bg-brand-blue/10',
  compiled: 'text-green-600 border-green-500/40 bg-green-50',
  link_sent: 'text-brand-blue border-brand-blue/40 bg-brand-blue/10',
  failed: 'text-red-600 border-red-500/40 bg-red-50',
};

// "Due" collapses the three non-terminal ExamConfig statuses into one
// admin-facing bucket - still outstanding, not yet delivered, not failed.
// "Completed" means status=link_sent (the exam was generated and the link
// emailed to the student) - not "the student has submitted it", which
// would need a call into quiz_service's attempt data that doesn't exist
// yet (docs/DECISIONS.md, 2026-07-29 "Admin dashboard exam-status menu").
export const DUE_STATUSES = ['pending', 'generating', 'compiled'];

export function summarizeConfigs(configs) {
  return {
    all: configs.length,
    due: configs.filter((c) => DUE_STATUSES.includes(c.status)).length,
    completed: configs.filter((c) => c.status === 'link_sent').length,
    failed: configs.filter((c) => c.status === 'failed').length,
  };
}

// --- Generate Question Prompt (docs/DECISIONS.md, 2026-08-04) ---
// Self-service admin tools for offline-authored question content. Prompt
// generation is pure client-side templating - no LLM call, nothing written anywhere -
// mirroring the largest-remainder split used server-side (bank_population.py's
// counts_per_level / exam_generator/selection.py's split_evenly).

export function splitEvenly(total, buckets) {
  if (buckets.length === 0) return {};
  const base = Math.floor(total / buckets.length);
  const remainder = total - base * buckets.length;
  const result = {};
  buckets.forEach((bucket, i) => {
    result[bucket] = base + (i < remainder ? 1 : 0);
  });
  return result;
}

export function splitByPercent(total, percentages) {
  const entries = Object.entries(percentages);
  const exact = entries.map(([label, pct]) => [label, (total * pct) / 100]);
  const floored = {};
  exact.forEach(([label, value]) => { floored[label] = Math.floor(value); });
  const flooredSum = Object.values(floored).reduce((a, b) => a + b, 0);
  const shortfall = total - flooredSum;
  const byRemainder = [...exact].sort((a, b) => (b[1] - Math.floor(b[1])) - (a[1] - Math.floor(a[1])));
  for (let i = 0; i < shortfall; i += 1) {
    floored[byRemainder[i][0]] += 1;
  }
  return floored;
}

export function buildQuestionPrompt(form) {
  const topics = splitList(form.topics);
  const total = Number(form.total_count);
  const pct = {
    easy: Number(form.easy_percent),
    medium: Number(form.medium_percent),
    tough: Number(form.tough_percent),
  };

  // Whole-syllabus mode (no topic list - the exam covers the full subject
  // syllabus) skips the per-topic breakdown
  // entirely instead of running splitEvenly/splitByPercent over an empty
  // topics list, which would otherwise silently produce a 0-question table.
  let countsSection;
  let taskLine;
  if (topics.length === 0) {
    const wholeSyllabusCounts = splitByPercent(total, pct);
    countsSection = `## Difficulty counts (whole syllabus - no fixed topic list for ${form.target_exam})
| Total | Easy | Medium | Tough |
|---|---|---|---|
| **${total}** | **${wholeSyllabusCounts.easy}** | **${wholeSyllabusCounts.medium}** | **${wholeSyllabusCounts.tough}** |`;
    taskLine = `Write exactly ${total} multiple-choice questions (4 options, single correct answer) covering the
**whole ${form.subject} syllabus for ${form.target_exam} Grade ${form.grade}** (not limited to a fixed topic list),
at the difficulty levels in the table below. Choose topics/sub-topics yourself, spread broadly across the syllabus
with no duplicated topic overused.`;
  } else {
    const perTopic = splitEvenly(total, topics);
    const rows = topics.map((topic) => {
      const counts = splitByPercent(perTopic[topic], pct);
      return { topic, total: perTopic[topic], ...counts };
    });
    const totals = rows.reduce(
      (acc, r) => ({ easy: acc.easy + r.easy, medium: acc.medium + r.medium, tough: acc.tough + r.tough }),
      { easy: 0, medium: 0, tough: 0 }
    );
    const tableLines = [
      '| Topic | Total | Easy | Medium | Tough |',
      '|---|---|---|---|---|',
      ...rows.map((r) => `| ${r.topic} | ${r.total} | ${r.easy} | ${r.medium} | ${r.tough} |`),
      `| **Total** | **${total}** | **${totals.easy}** | **${totals.medium}** | **${totals.tough}** |`,
    ];
    countsSection = `## Topic / difficulty counts\n${tableLines.join('\n')}`;
    taskLine = `Write exactly ${total} multiple-choice questions (4 options, single correct answer) split across the topics and
difficulty levels in the table below.`;
  }

  return `You are authoring exam questions for **${form.target_exam}** (Grade ${form.grade}), subject **${form.subject}**.

## Exam context
${form.pattern_notes.trim() || '(No additional exam-pattern notes provided.)'}

## Task
${taskLine}

Difficulty calibration:
- **easy** - direct recall / single-step application.
- **medium** - multi-step application, standard variation.
- **tough** - multi-concept, analytical, non-obvious.

${countsSection}

## Output format
Return a single JSON array, one object per question, matching this exact schema:

\`\`\`json
{
  "chapter": "<same as topic>",
  "topic": "<one of the topics above, exactly as written>",
  "sub_topic": "<a more specific sub-topic within it>",
  "difficulty": "easy" | "medium" | "tough",
  "question_type": "MCQ",
  "text": "Question text goes here...",
  "options": { "a": "...", "b": "...", "c": "...", "d": "..." },
  "correct_option": "a",
  "answer": "Full text of the correct option (mirrors options[correct_option])",
  "explanation": "Step-by-step reasoning for the correct answer.",
  "hint": "A short nudge, not the answer.",
  "bloom_level": "Remember" | "Understand" | "Apply" | "Analyze" | "Evaluate" | "Create",
  "estimated_solve_time_minutes": <integer minutes, typical time to solve>
}
\`\`\`

Rules:
- No duplicate questions, within a topic or across the whole file.
- Every question must have exactly one unambiguously correct option.
- \`correct_option\` must be a key in \`options\`, and \`answer\` must match that option's text exactly.
- Do not include \`subject\`, \`grade\`, or \`target_exam\` in each record - the Upload Questions tool sets those
  once for the whole batch, not per question.
`;
}
